import { cn } from 'component-library/utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRating,
  PlotReportFactLabelNameEnum,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { safeRiskIndexToRating } from '@/utils/climateRisk';
import { getIconForClimateRiskRating, getStyleForClimateRiskRating } from '@/utils/getStyleForClimateRisk';

// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '../widgets/WidgetSlotRenderer';

type RiskInfoProps = {
  selectedRiskFactType: string;
  riskFactType: CLIMATE_RISK_FACT_TYPES;
  onSelectRisk?: (risk: string) => void;
};

export const RiskInfo = (props: RiskInfoProps) => {
  const { plotId } = useParams<{ plotId: string }>();

  if (plotId) {
    return <PlotRiskInfo {...props} />;
  }

  return <OrgRiskInfo {...props} />;
};

export const PlotRiskInfo = (props: RiskInfoProps) => {
  const facts = usePlotFacts<number>([props.riskFactType]);

  const riskFact = getFactFromFactArray(facts, props.riskFactType, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const riskRatingType = (riskFact?.value as Record<string, any>)?.rating;
  if (isNullish(riskFact)) return null;
  return <CommonRiskInfo {...props} riskRatingType={riskRatingType} />;
};

export const OrgRiskInfo = (props: RiskInfoProps) => {
  const { [props.riskFactType]: riskFact } = useOrganizationIndicatorReportingFact([props.riskFactType], null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });
  const riskRatingType = safeRiskIndexToRating(riskFact?.value as number);
  if (isNullish(riskFact)) return null;
  return <CommonRiskInfo {...props} riskRatingType={riskRatingType} />;
};

type CommonRiskInfoProps = RiskInfoProps & {
  riskRatingType: ClimateRiskRating;
};

const CommonRiskInfo = ({ riskFactType, riskRatingType, selectedRiskFactType, onSelectRisk }: CommonRiskInfoProps) => {
  const { t } = useTranslation();

  // TODO: MVP-CW lokalise
  const riskLevel = `${riskRatingType.replace('_', ' ')} risk`;

  const riskTrendIcon = getIconForClimateRiskRating(riskRatingType);

  const style = getStyleForClimateRiskRating(riskRatingType);

  const config = {
    name: `risk_info_${riskFactType}`,
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: cn(
        'px-3 my-4 ',
        riskFactType === selectedRiskFactType
          ? 'border-l-4 border-primary-100 font-semibold text-primary-100'
          : 'hover:cursor-pointer hover:bg-neutral-black-4 hover:py-4 hover:my-0 hover:rounded-2xl',
      ),
      onClick: () => onSelectRisk?.(riskFactType),
    },
    widgets: [
      {
        name: `risk_preview_${riskFactType}`,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex-col gap-1 md:flex-row items-center justify-between',
        },
        widgets: [
          {
            name: `risk_${riskFactType}`,
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              className: 'flex flex-row',
            },
            widgets: [
              {
                name: `risk_name_${riskFactType}`,
                type: WidgetType.a_text,
                visible: true,
                relatedData: {
                  text: t(`climateRisk.riskTypes.${riskFactType}.title`),
                  className: 'typography-h4',
                },
              },
              {
                name: `risk_name_tooltip_${riskFactType}`,
                type: WidgetType.m_tooltip,
                visible: true,
                relatedData: {},
                widgets: [
                  {
                    name: 'risk_name_tooltip_trigger',
                    type: WidgetType.m_tooltip_trigger,
                    visible: true,
                    widgets: [
                      {
                        name: 'risk_name_tooltip_icon',
                        type: WidgetType.a_icon,
                        visible: true,
                        relatedData: {
                          icon: 'ri-information-line',
                          className: 'ml-1 text-active-54 typography-body1',
                        },
                      },
                    ],
                  },
                  {
                    name: 'risk_name_tooltip_content',
                    type: WidgetType.m_tooltip_content,
                    visible: true,
                    widgets: [
                      {
                        name: 'risk_name_tooltip_text',
                        type: WidgetType.a_text,
                        visible: true,
                        relatedData: {
                          i18nPath: `climateRisk.riskTypes.${riskFactType}.tooltip.body`,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },

          {
            name: `risk_trend_icon_${riskFactType}`,
            type: WidgetType.m_icon_button,
            visible: true,
            relatedData: {
              icon: riskTrendIcon,
              text: riskLevel,
              className: `typography-body2 font-semibold w-fit text-error-dark px-4 py-2 capitalize`,
              style: {
                color: style?.text,
                backgroundColor: style?.primary,
              },
            },
          },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={config} />;
};
